import { ScrollableArea } from "@nova/component-scrollable-area"
import * as Icons from "@nova/component-icons"
import {
    FunctionComponent,
    HTMLProps,
    PropsWithChildren,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { InfraredFlight, InfraredImage } from "@nova/protobuf"
import { HotspotWithLabel, InfraredImageWithContext } from "@nova/constants"
import classNames from "classnames"
import { setHotspotPanelState, toggleShowRemovedFilter, toggleWithHotspotsFilter } from "./slice"
import { selectHotspotPanelFilters, selectHotspotPanelImages } from "./selectors"
import { Popup } from "@nova/component-popup"
import { Checkbox } from "@nova/component-checkbox"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { getInfraredBoundingBox } from "plugins/InfraredImage/useInfraredImageBoundingBox"
import { zoomToShowPoint } from "features/map/control/slice"
import {
    selectNonRemovedHotspotImageIdMap,
    selectRemovedHotspotsImageIdMap,
} from "features/entity-caching/hotspots/selectors"

interface HotspotPanelListProps {
    flight: InfraredFlight
    image: InfraredImage | undefined
    hotspot: HotspotWithLabel | undefined
}

const HotspotPanelList: FunctionComponent<HotspotPanelListProps> = ({ flight, image, hotspot }) => {
    const images = useAppSelector(selectHotspotPanelImages(flight.id))
    const imageMap = useAppSelector(selectNonRemovedHotspotImageIdMap(flight.id))
    const removedHotspotMap = useAppSelector(selectRemovedHotspotsImageIdMap(flight.id))

    return (
        <div className="flex flex-col text-gray-100">
            <div className="flex justify-between p-2 bg-gray-800 rounded-t-lg max-h-[50px] h-full items-center">
                <h5 className="text-gray-50 pl-1">{images.length} Files</h5>
                <Popup
                    content={
                        <Popup.PopupModal>
                            <ListFilters />
                        </Popup.PopupModal>
                    }
                >
                    <Icons.FilterList height={20} width={20} />
                </Popup>
            </div>

            <ScrollableArea className="h-full">
                <div className="p-2 pt-0">
                    <ImageList
                        imageMap={imageMap}
                        selectedImage={image}
                        images={images}
                        selectedHotspot={hotspot}
                        removedHotspotMap={removedHotspotMap}
                    />
                </div>
            </ScrollableArea>
        </div>
    )
}

const ListFilters = () => {
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectHotspotPanelFilters)

    return (
        <div className="flex flex-col gap-2">
            <h5>Filters</h5>
            <div
                className="flex gap-3 text-gray-0 cursor-pointer"
                onClick={() => dispatch(toggleWithHotspotsFilter())}
            >
                <Checkbox isChecked={filters.withHotspots} onClick={() => {}} />
                <h5 className="select-none">With Hotspots</h5>
            </div>
            <div
                className="flex gap-3 text-gray-0 cursor-pointer"
                onClick={() => dispatch(toggleShowRemovedFilter())}
            >
                <Checkbox isChecked={filters.showRemoved} onClick={() => {}} />
                <h5 className="select-none">Show Removed</h5>
            </div>
        </div>
    )
}

const ImageList = ({
    images,
    selectedImage,
    imageMap,
    selectedHotspot,
    removedHotspotMap,
}: {
    images: InfraredImageWithContext[]
    selectedImage: InfraredImage | undefined
    imageMap: Map<string, HotspotWithLabel[]>
    removedHotspotMap: Map<string, HotspotWithLabel[]>
    selectedHotspot: HotspotWithLabel | undefined
}) => {
    const [manualHotspotsOpen, setManualHotspotsOpen] = useState(!selectedImage)
    const dispatch = useAppDispatch()
    const hotspotPanelFilters = useAppSelector(selectHotspotPanelFilters)

    const handleImageClick = (image: InfraredImage) => {
        getInfraredBoundingBox(image, (res) => {
            dispatch(zoomToShowPoints({ points: res as [number, number][], preserveZoom: true }))
        })
        dispatch(setHotspotPanelState({ imageId: image.id, hotspotId: undefined }))
    }
    const handleHotspotClick = (hotspot: HotspotWithLabel) => {
        dispatch(
            zoomToShowPoint({
                lng: hotspot.longitude,
                lat: hotspot.latitude,
                preserveZoom: true,
            })
        )
        dispatch(setHotspotPanelState({ imageId: hotspot.imageId, hotspotId: hotspot.id }))
    }

    const isActiveImage = (image: InfraredImage) => image.id === selectedImage?.id

    const imageHasRemovedHotspots = (imageId: string) => removedHotspotMap.has(imageId)
    const isActiveHotspot = (hotspot: HotspotWithLabel) => hotspot.id === selectedHotspot?.id

    const imageIdHasHotspots = (imageId: string) => {
        const hasHotspots = imageMap.has(imageId)

        const hasRemovedHotspots = removedHotspotMap.has(imageId)

        return hasHotspots || (hotspotPanelFilters.showRemoved && hasRemovedHotspots)
    }

    return (
        <div className="flex flex-col">
            {imageIdHasHotspots("mock") && (
                <>
                    <ListItem
                        active={manualHotspotsOpen}
                        onClick={() => {
                            setManualHotspotsOpen((prev) => !prev)
                        }}
                        className={classNames(manualHotspotsOpen && "rounded-b-none")}
                        title="Manual Hotspots"
                        leftIcon={
                            <div
                                className={classNames(
                                    "border-[2px] border-transparent rounded-[3px] w-2 h-2"
                                )}
                            />
                        }
                    />
                    {(manualHotspotsOpen || !selectedImage) && (
                        <div className="pl-4 bg-blue-900 rounded-b-md">
                            {imageMap.get("mock")?.map((hotspot) => (
                                <ListItem
                                    key={hotspot.id}
                                    className={classNames(
                                        "hover:bg-blue-900",
                                        isActiveHotspot(hotspot) && "rounded-r-none"
                                    )}
                                    active={isActiveHotspot(hotspot)}
                                    onClick={() => handleHotspotClick(hotspot)}
                                    title={hotspot.label}
                                />
                            ))}
                            {manualHotspotsOpen &&
                                imageHasRemovedHotspots("mock") &&
                                hotspotPanelFilters.showRemoved && (
                                    <div className="border-t border-white-200">
                                        {removedHotspotMap.get("mock")?.map((hotspot) => (
                                            <ListItem
                                                key={"removed-mock-" + hotspot.id}
                                                className={classNames(
                                                    "hover:bg-blue-900",
                                                    isActiveHotspot(hotspot) && "rounded-r-none"
                                                )}
                                                active={isActiveHotspot(hotspot)}
                                                onClick={() => handleHotspotClick(hotspot)}
                                                title={`${hotspot.label} (Removed)`}
                                            />
                                        ))}
                                    </div>
                                )}
                        </div>
                    )}
                </>
            )}
            {images.map((image, i) => (
                <>
                    <ListItem
                        key={i}
                        onClick={() => {
                            setManualHotspotsOpen(false)
                            handleImageClick(image)
                        }}
                        title={shortenFileName(image.fileName)}
                        active={isActiveImage(image)}
                        className={classNames(
                            isActiveImage(image) && imageIdHasHotspots(image.id) && "rounded-b-none"
                        )}
                        leftIcon={
                            <div
                                className={classNames(
                                    "border-[2px] border-transparent rounded-[3px] w-2 h-2",
                                    imageIdHasHotspots(image.id) && "border-red-500 "
                                )}
                            />
                        }
                    />
                    {isActiveImage(image) && imageIdHasHotspots(image.id) && (
                        <div className="pl-4 bg-blue-900 rounded-b-md">
                            {imageMap.get(image.id)?.map((hotspot, hI) => (
                                <ListItem
                                    key={`${i}-${hI}`}
                                    active={isActiveHotspot(hotspot)}
                                    onClick={() => handleHotspotClick(hotspot)}
                                    className={classNames(
                                        "hover:bg-blue-900",
                                        isActiveHotspot(hotspot) && "rounded-r-none"
                                    )}
                                    title={hotspot.label}
                                />
                            ))}
                            {isActiveImage(image) &&
                                imageHasRemovedHotspots(image.id) &&
                                hotspotPanelFilters.showRemoved && (
                                    <div className="border-t border-white-200">
                                        {removedHotspotMap.get(image.id)?.map((hotspot, hI) => (
                                            <ListItem
                                                key={"removed-" + `${i}-${hI}`}
                                                className={classNames(
                                                    "hover:bg-blue-900",
                                                    isActiveHotspot(hotspot) && "rounded-r-none"
                                                )}
                                                active={isActiveHotspot(hotspot)}
                                                onClick={() => handleHotspotClick(hotspot)}
                                                title={`${hotspot.label} (Removed)`}
                                            />
                                        ))}
                                    </div>
                                )}
                        </div>
                    )}
                </>
            ))}
        </div>
    )
}

const ListItem = ({
    title,
    active,
    onClick,
    leftIcon,
    className,
    ...rest
}: PropsWithChildren<{
    active: boolean
    onClick: () => void
    title: string
    leftIcon?: ReactNode
}> &
    HTMLProps<HTMLDivElement>) => {
    const selfRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (active && selfRef.current) selfRef.current.scrollIntoView({ block: "nearest" })
    }, [active])

    return (
        <div className={classNames("flex flex-col")} ref={selfRef}>
            <div
                onClick={() => {
                    onClick()
                }}
                className={classNames(
                    "hover:bg-gray-700 hover:text-white-1000 cursor-pointer w-full p-1 rounded-md flex gap-2 items-center",
                    {
                        "text-white-1000 !bg-blue-700": active,
                    },
                    className
                )}
                {...rest}
            >
                {leftIcon}
                <small>{title}</small>
            </div>
        </div>
    )
}

const shortenFileName = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf(".")
    const name = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName
    const ext = dotIndex > 0 ? fileName.slice(dotIndex) : ""

    if (name.length <= 15) {
        return fileName
    }

    return `${name.slice(0, 4)}...${name.slice(-5)}${ext}`
}

export { HotspotPanelList }
